import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";

import Chart from "../../utils/chart";

function BatchByPeriod({
  chartOptions = {},
  title = "Batch Overview by Period",
  chartData = {},
  labels = [],
  completed = [],
  failed = [],
  pending = [],
}) {
  const canvasRef = useRef();

  useEffect(() => {
    if (labels.length) configureChart(computeStepSize(labels.length));
  }, [labels]);

  function computeStepSize(labelLen) {
    return labelLen <= 7
      ? 1
      : labelLen > 7 && labelLen <= 35
      ? 7
      : labelLen > 35 && labelLen <= 60
      ? 10
      : labelLen > 60 && 90
      ? 15
      : 30;
  }

  function getLineChartOptions(xStep) {
    return {
      ...{
        responsive: true,
        legend: {
          position: "top",
        },
        elements: {
          line: {
            // A higher value makes the line look skewed at this ratio.
            tension: 0.3,
          },
          point: {
            radius: 0,
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: false,
              ticks: {
                callback(tick, index) {
                  // Jump every {x-step} values on the X axis labels to avoid clutter.
                  return index % xStep !== 0 ? "" : tick;
                },
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                suggestedMax: 45,
                callback(tick) {
                  if (tick === 0) {
                    return tick;
                  }
                  // Format the amounts using Ks for thousands.
                  return tick > 999 ? `${(tick / 1000).toFixed(1)}K` : tick;
                },
              },
            },
          ],
        },
        hover: {
          mode: "nearest",
          intersect: false,
        },
        tooltips: {
          custom: false,
          mode: "nearest",
          intersect: false,
        },
      },
      ...chartOptions,
    };
  }

  function configureChart(xStepSize) {
    const chData = JSON.parse(JSON.stringify(chartData));
    chData.labels = labels;
    chData.datasets[0].data = completed;
    chData.datasets[1].data = failed;
    chData.datasets[2].data = pending;

    const MultiLineChart = new Chart(canvasRef.current, {
      type: "LineWithLine",
      data: chData,
      options: getLineChartOptions(xStepSize),
    });

    // They can still be triggered on hover.
    /* const buoMeta = MultiLineChart.getDatasetMeta(0);
    buoMeta.data[0]._model.radius = 0;
    buoMeta.data[chData.datasets[0].data.length - 1]._model.radius = 0; */

    // Render the chart.
    MultiLineChart.render();
  }

  return (
    <Card className="h-100">
      <Card.Header className="border-bottom">
        <h6 className="m-0">{title}</h6>
      </Card.Header>
      <Card.Body className="pt-0">
        <canvas
          height="120"
          ref={canvasRef}
          style={{ maxWidth: "100% !important" }}
        />
      </Card.Body>
    </Card>
  );
}

BatchByPeriod.propTypes = {
  /**
   * The component's title.
   */
  title: PropTypes.string,
  /**
   * The chart dataset.
   */
  chartData: PropTypes.object,
  /**
   * The Chart.js options.
   */
  chartOptions: PropTypes.object,
};

BatchByPeriod.defaultProps = {
  title: "Monthly Overview",
  chartData: {
    labels: [],
    datasets: [
      {
        label: "Completed",
        fill: "start",
        data: [],
        backgroundColor: "rgba(0,123,255,0.1)",
        borderColor: "rgba(0,123,255,1)",
        pointBackgroundColor: "#ffffff",
        pointHoverBackgroundColor: "rgb(0,123,255)",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
      },
      {
        label: "Failed",
        fill: "start",
        data: [],
        backgroundColor: "rgba(255,65,105,0.1)",
        borderColor: "rgba(255,65,105,1)",
        pointBackgroundColor: "#ffffff",
        pointHoverBackgroundColor: "rgba(255,65,105,1)",
        borderDash: [3, 3],
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 2,
        pointBorderColor: "rgba(255,65,105,1)",
      },
      {
        label: "Pending",
        fill: "start",
        data: [],
        backgroundColor: "rgba(32,201,151,0.1)",
        borderColor: "rgba(32,201,151,1)",
        pointBackgroundColor: "#ffffff",
        pointHoverBackgroundColor: "rgba(32,201,151,1)",
        borderDash: [3, 3],
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 2,
        pointBorderColor: "rgba(32,201,151,1)",
      },
    ],
  },
};

export default BatchByPeriod;
